import * as React from "react";
import { useLocation, useNavigate } from "react-router";

import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import type { FirebaseError } from "firebase/app";
import {
    getAuth,
    signOut as firebaseSignOut,
    onAuthStateChanged,
    User as FirebaseUser,
    signInWithCustomToken,
} from "firebase/auth";

import { OffsetResult, Subscription } from "@volley/data";
import type { UserWithContentProviders } from "@volley/data";

import { clearAuthToken, getAuthToken } from "../../util/authToken";
import fetchApi, { logFetchError } from "../../util/fetchApi";

export interface UserRole {
    role: { name: string };
}

export enum AuthState {
    LOADING,
    UNAUTHENTICATED,
    AUTHENTICATED,
}

interface Value {
    currentUser: UserWithContentProviders | null;
    setCurrentUser: React.Dispatch<
        React.SetStateAction<UserWithContentProviders | null>
    >;
    authState: AuthState;
    subscriptions: Subscription[];
    refreshSubscriptions: () => void;
    error?: string;
    features: string[];
    featuresLoaded: boolean;
    /** Load features again, e.g. after signup put the member in segments. */
    reloadFeatures: () => void;
    isAdmin: () => boolean;
    isPro: () => boolean;
    isAssessmentTester: () => boolean;
    signOut: () => void;
}

const DEFAULT_VALUE: Value = {
    currentUser: null,
    features: [],
    featuresLoaded: false,
    reloadFeatures: () => {},
    setCurrentUser: () => {},
    authState: AuthState.LOADING,
    subscriptions: [],
    refreshSubscriptions: () => {},
    isAdmin: () => false,
    isPro: () => false,
    isAssessmentTester: () => false,
    signOut: () => {},
};

const VALID_SUBSCRIPTION_STATUSES = new Set([
    "active",
    "past_due",
    "incomplete",
]);

export const CurrentUserContext = React.createContext<Value>(DEFAULT_VALUE);

function hasRole(roles: UserRole[], role: string): boolean {
    const matchingRoles = roles.filter((r) => r.role.name === role);
    return matchingRoles.length > 0;
}

/**
 * Check if a user has an active subscription. First, user's with an ADMIN role are always
 * considered to have an active subscription. If the user does not have an ADMIN role, then
 * their subscriptions are checked; if one of them meets the criteria of being active, then
 * this function returns true.
 *
 * @param user the user to check whether they're an admin
 * @param subscriptions the subscriptions to check if one is active
 * @returns is the user an admin OR does the user have an active subscription
 */
export function hasActiveSubscription(
    user: UserWithContentProviders | null,
    subscriptions: Subscription[],
): boolean {
    return (
        (user && hasRole(user.userRoles, "ADMIN")) ||
        subscriptions.some((s) => VALID_SUBSCRIPTION_STATUSES.has(s.status))
    );
}

interface Props {
    children: React.ReactNode;
}

interface LocationState {
    from?: string;
}

type AuthenticationResponse = UserWithContentProviders & {
    isNewUser: boolean;
};

export function CurrentUserProvider({ children }: Props): React.JSX.Element {
    const navigate = useNavigate();
    const location = useLocation();
    // Captured out of the URL at startup; cleared once exchanged for a
    // Firebase session, which mirrors the param disappearing from the URL.
    const [token, setToken] = React.useState<string | null>(getAuthToken);
    const [authState, setAuthState] = React.useState(AuthState.LOADING);
    const [user, setUser] = React.useState<UserWithContentProviders | null>(
        null,
    );
    const [isNewUser, setIsNewUser] = React.useState(false);
    const [features, setFeatures] = React.useState<string[] | null>(null);
    const [subscriptions, setSubscriptions] = React.useState<Subscription[]>(
        [],
    );
    const [error, setError] = React.useState<string>();

    const signOut = React.useCallback(async () => {
        await firebaseSignOut(getAuth());
        setUser(null);
    }, []);

    const handleAuthStateChange = React.useCallback(
        async (authUser: FirebaseUser | null) => {
            if (!authUser) {
                setUser(null);
                if (!token) setAuthState(AuthState.UNAUTHENTICATED);
                return;
            }

            const idToken = await authUser.getIdToken();

            let volleyUser: UserWithContentProviders;
            try {
                const response = await fetchApi<AuthenticationResponse>(
                    "/api/users/authentication",
                    "POST",
                    { idToken },
                );
                volleyUser = response;
                if (response.isNewUser) setIsNewUser(true);
            } catch (e: unknown) {
                setError((e as Error).message);
                return;
            }

            setUser(volleyUser);
            const monitoredUser = {
                id: String(volleyUser.id),
                email: volleyUser.email,
                name: `${volleyUser.firstName} ${volleyUser.lastName}`,
                username: volleyUser.username,
            };
            datadogRum.setUser(monitoredUser);
            datadogLogs.setUser(monitoredUser);
            setError(undefined);
            setAuthState(AuthState.AUTHENTICATED);
        },
        [token],
    );

    const refreshSubscriptions = React.useCallback(() => {
        if (!user?.id) return;

        fetchApi<OffsetResult<Subscription>>(
            `/api/users/${user.id}/subscriptions?limit=10&offset=0`,
        )
            .then(({ result }) => {
                setSubscriptions(result);
            })
            .catch(logFetchError);
    }, [user?.id]);

    React.useEffect(() => {
        refreshSubscriptions();
    }, [refreshSubscriptions]);

    const value = React.useMemo<Value>(
        () => ({
            currentUser: user,
            authState,
            subscriptions,
            refreshSubscriptions,
            error,
            features: features ?? [],
            featuresLoaded: features !== null,
            // Clearing them refetches below, and featuresLoaded is false
            // until the new list arrives.
            reloadFeatures: () => setFeatures(null),
            setCurrentUser: setUser,
            isAdmin: () => user !== null && hasRole(user.userRoles, "ADMIN"),
            isPro: () => user !== null && hasRole(user.userRoles, "PRO"),
            isAssessmentTester: () =>
                user !== null && hasRole(user.userRoles, "ASSESSMENTS_TESTER"),
            signOut,
        }),
        [
            features,
            subscriptions,
            refreshSubscriptions,
            user,
            authState,
            error,
            signOut,
        ],
    );

    // Observe changes to authentication state, stop listening on unmount
    React.useEffect(
        () => onAuthStateChanged(getAuth(), handleAuthStateChange),
        [handleAuthStateChange],
    );

    // Sign in with custom auth token
    React.useEffect(() => {
        const auth = getAuth();
        if (token && !auth.currentUser) {
            signInWithCustomToken(auth, token)
                .then(() => {
                    clearAuthToken();
                    setToken(null);
                })
                .catch((err: FirebaseError) => {
                    setError(err.message);
                });
        }
    }, [token]);

    // Direct user into application after login
    React.useEffect(() => {
        if (
            user &&
            authState === AuthState.AUTHENTICATED &&
            location.pathname.startsWith("/login")
        ) {
            const from = (location.state as LocationState | null)?.from;
            if (isNewUser) {
                // A player who opened a claim link and then signed in with a
                // provider for the first time is signing up: carry the code
                // on so the account comes with their assessment.
                const claimCode = from?.match(/^\/claim\/([^/?#]+)/)?.[1];
                const signupPath = claimCode
                    ? `/signup/account?invite=${encodeURIComponent(claimCode)}`
                    : "/signup/account";
                navigate(signupPath, {
                    replace: true,
                    state: {
                        email: user.email,
                        name: `${user.firstName} ${user.lastName}`,
                    },
                });
            } else {
                navigate(from ?? "/", { replace: true });
            }
        }
    }, [
        user,
        isNewUser,
        authState,
        location.pathname,
        location.state,
        navigate,
    ]);

    React.useEffect(() => {
        async function fetchFeatures(id: number) {
            const results: string[] = await fetchApi(
                `/api/users/${id}/features`,
                "GET",
            );
            setFeatures(results);
        }

        if (
            authState === AuthState.AUTHENTICATED &&
            user?.id &&
            features === null
        ) {
            void fetchFeatures(user.id);
        }
    }, [features, authState, user?.id]);

    return (
        <CurrentUserContext.Provider value={value}>
            {children}
        </CurrentUserContext.Provider>
    );
}

export function useCurrentUser(): Value {
    return React.useContext(CurrentUserContext);
}
